import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Statistic } from 'antd';
import { UserOutlined, KeyOutlined, PartitionOutlined, WifiOutlined } from '@ant-design/icons';
import { usePermissions } from '@zero/auth-client';
import { createAuthApi } from '@zero/auth-admin-ui';
import { useTranslation } from 'react-i18next';

/**
 * Dashboard demo — số liệu tổng quan generic (không leak Marine-specific).
 *
 * 4 stat cards:
 *   - User ID
 *   - Số permission được cấp
 *   - Số scope-assignment (gộp tất cả scope key)
 *   - Số user đang online (hệ thống — distinct active session trong 15 phút)
 *
 * "My Permissions" detail đã chuyển sang trang Tài khoản (/profile).
 */
export function DashboardPage({ axios }) {
  const { context } = usePermissions();
  const { t } = useTranslation();
  const api = useMemo(() => axios ? createAuthApi(axios) : null, [axios]);
  const [onlineCount, setOnlineCount] = useState(null);

  useEffect(() => {
    if (!api) return;
    let alive = true;
    const fetch = () => api.getDashboardStats()
      .then((r) => alive && setOnlineCount(r.data?.Data?.OnlineUsersCount ?? 0))
      .catch(() => alive && setOnlineCount(null));
    fetch();
    // Auto refresh mỗi 30s — dashboard live-ish
    const id = setInterval(fetch, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [api]);

  const scopeCount = useMemo(() => {
    if (!context) return 0;
    return (context.DepartmentIDs?.length ?? 0) + (context.VesselIDs?.length ?? 0);
  }, [context]);

  if (!context) return null;

  return (
    <Row gutter={[4, 4]}>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Statistic title={t('app:dashboard.userId')} value={context.UserID} prefix={<UserOutlined />} />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Statistic title={t('app:dashboard.permissions')} value={context.Permissions?.length ?? 0} prefix={<KeyOutlined />} />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Statistic title={t('app:dashboard.scopeAssignments')} value={scopeCount} prefix={<PartitionOutlined />} />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Statistic
            title={t('app:dashboard.onlineUsers')}
            value={onlineCount ?? '–'}
            prefix={<WifiOutlined style={{ color: onlineCount > 0 ? '#52c41a' : undefined }} />}
            suffix={onlineCount != null ? <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>{t('app:dashboard.onlineWindow')}</span> : null}
          />
        </Card>
      </Col>
    </Row>
  );
}
